// Central content for the PRIMEC2E site (French)
import {
  Snowflake, Flame, Wind, Lightbulb, Cpu, Sun, Gauge,
} from "lucide-react";

export const COMPANY = {
  name: "PRIMEC2E",
  baseline: "Performance énergétique des bâtiments tertiaires",
  phone: "09 74 37 80 68",
  phoneRaw: "0974378068",
  email: "contact@primec2e.info",
  address: "Tour Coupole, La Défense",
  addressFull: "Tour Coupole, 2 place de la Coupole Jean Millier, 92400 Courbevoie",
  city: "Courbevoie",
};

export const STATS = [
  { value: "40%", label: "d'économies d'énergie réalisables", sub: "sur vos consommations" },
  { value: "100%", label: "de certains travaux financés via les CEE", sub: "selon dispositifs et éligibilité" },
  { value: "A→Z", label: "Accompagnement complet", sub: "audit, dossier CEE et suivi" },
];

export const TRAVAUX = [
  {
    id: "isolation",
    icon: Snowflake,
    title: "Isolation",
    featured: true,
    items: ["Toitures", "Combles", "Murs", "Planchers"],
    description:
      "L'isolation de l'enveloppe du bâtiment est le premier levier d'économies. Elle réduit durablement les déperditions thermiques et améliore le confort des occupants.",
    benefits: "Confort thermique, réduction des pertes, valorisation du patrimoine.",
    economy: 30,
    cee: true,
  },
  {
    id: "chauffage",
    icon: Flame,
    title: "Chauffage",
    items: ["Pompes à chaleur", "Chaudières performantes", "Réseaux hydrauliques"],
    description:
      "Le remplacement des systèmes de chauffage vétustes par des équipements haute performance génère des gains immédiats.",
    benefits: "Rendement élevé, énergies décarbonées, maintenance réduite.",
    economy: 35,
    cee: true,
  },
  {
    id: "ventilation",
    icon: Wind,
    title: "Ventilation",
    items: ["Ventilation double flux", "Optimisation des débits"],
    description:
      "Une ventilation maîtrisée assure une qualité d'air optimale tout en récupérant la chaleur de l'air extrait.",
    benefits: "Qualité d'air, récupération de chaleur, conformité sanitaire.",
    economy: 20,
    cee: true,
  },
  {
    id: "eclairage",
    icon: Lightbulb,
    title: "Éclairage",
    items: ["LED", "Détection de présence", "Gestion intelligente"],
    description:
      "Le passage au LET associé à la gestion intelligente divise la facture d'éclairage tout en améliorant le confort visuel.",
    benefits: "Jusqu'à 60% d'économie sur l'éclairage, durée de vie accrue.",
    economy: 25,
    cee: true,
  },
  {
    id: "regulation",
    icon: Cpu,
    title: "Régulation & GTB",
    featured: true,
    items: ["GTB (Gestion Technique du Bâtiment)", "Thermostats intelligents", "Pilotage énergétique"],
    description:
      "La Gestion Technique du Bâtiment centralise le pilotage des équipements pour optimiser en continu les consommations. Un levier clé de conformité au Décret Tertiaire.",
    benefits: "Pilotage temps réel, économies sans travaux lourds, données de suivi.",
    economy: 30,
    cee: true,
  },
  {
    id: "production",
    icon: Sun,
    title: "Production d'énergie",
    items: ["Photovoltaïque", "Solaire thermique"],
    description:
      "La production d'énergie renouvelable sur site réduit la dépendance au réseau et sécurise vos coûts énergétiques.",
    benefits: "Autoconsommation, décarbonation, revenus complémentaires.",
    economy: 25,
    cee: false,
  },
  {
    id: "optimisation",
    icon: Gauge,
    title: "Optimisation énergétique",
    items: ["Variateurs de vitesse", "Récupération de chaleur", "Optimisation des équipements"],
    description:
      "L'optimisation fine des équipements existants offre un retour sur investissement rapide sans remplacement complet.",
    benefits: "ROI rapide, gains continus, moindre usure des équipements.",
    economy: 20,
    cee: true,
  },
];

export const PARCOURS = [
  { n: "01", title: "Audit énergétique", text: "Analyse complète de vos consommations, de vos équipements et de votre bâti pour identifier les gisements d'économies." },
  { n: "02", title: "Étude de faisabilité", text: "Modélisation technique et financière des scénarios de travaux les plus pertinents pour votre patrimoine." },
  { n: "03", title: "Recherche des financements", text: "Identification de tous les dispositifs mobilisables : CEE, aides territoriales et financements adaptés." },
  { n: "04", title: "Montage du dossier CEE", text: "Nous prenons en charge l'intégralité du dossier administratif, du calcul des primes au dépôt." },
  { n: "05", title: "Réalisation des travaux", text: "Pilotage et coordination des entreprises qualifiées, dans le respect des engagements de performance." },
  { n: "06", title: "Mesure des performances", text: "Instrumentation et mesure des consommations pour vérifier l'atteinte des objectifs contractuels." },
  { n: "07", title: "Suivi des économies", text: "Suivi dans la durée via la GTB et reporting régulier pour garantir les économies dans le temps." },
];

export const AVANTAGES = [
  { title: "Expertise réglementaire", text: "Maîtrise du Décret Tertiaire, du dispositif CEE et des obligations applicables à votre patrimoine." },
  { title: "Maîtrise des CEE", text: "Optimisation du montage financier pour maximiser les primes mobilisables sur vos travaux." },
  { title: "Garantie de performance", text: "Engagement contractuel sur les économies réalisées, mesurées et vérifiées." },
  { title: "Accompagnement administratif", text: "Prise en charge intégrale des démarches et dossiers CEE, de A à Z." },
  { title: "Interlocuteur unique", text: "Un seul contact pour piloter l'ensemble de votre projet de performance énergétique." },
  { title: "Réduction durable", text: "Des économies pérennes grâce au suivi continu et au pilotage énergétique." },
];

export const FAQ = [
  { q: "Qu'est-ce qu'un CPE ?", a: "Le Contrat de Performance Énergétique est un contrat par lequel un prestataire s'engage contractuellement sur un niveau d'économies d'énergie mesuré et vérifié. Il aligne les intérêts du prestataire et du maître d'ouvrage autour d'un résultat garanti." },
  { q: "Qui est concerné par le Décret Tertiaire ?", a: "Tous les bâtiments à usage tertiaire (bureaux, commerces, enseignement, santé, logistique…) dont la surface est supérieure ou égale à 1 000 m², qu'ils soient privés ou publics." },
  { q: "Quels travaux sont éligibles aux CEE ?", a: "L'isolation, le chauffage performant, la ventilation, l'éclairage LED, la régulation (GTB), la récupération de chaleur et de nombreux autres postes définis par les fiches d'opérations standardisées." },
  { q: "Les CEE financent-ils la totalité des travaux ?", a: "Certains travaux peuvent être financés jusqu'à 100% selon les dispositifs et l'éligibilité. Le taux de financement dépend de la fiche CEE, du volume d'économies et des primes en vigueur." },
  { q: "Combien de temps dure un CPE ?", a: "Un CPE dure généralement entre 5 et 15 ans, selon l'ampleur des investissements et la durée nécessaire pour garantir et mesurer les économies." },
  { q: "Comment est calculée la performance ?", a: "La performance est mesurée par comparaison entre une situation de référence (avant travaux) et les consommations réelles après travaux, avec des ajustements climatiques et d'usage." },
  { q: "Qui peut bénéficier des CEE ?", a: "Les entreprises, collectivités, gestionnaires de patrimoine et propriétaires de bâtiments tertiaires réalisant des travaux d'économies d'énergie éligibles." },
  { q: "Les collectivités sont-elles concernées ?", a: "Oui. Les collectivités sont pleinement concernées par le Décret Tertiaire et peuvent mobiliser les CEE pour financer la rénovation de leur patrimoine public." },
  { q: "Comment démarrer un projet ?", a: "Tout commence par un audit énergétique. Demandez votre audit via notre formulaire : nous analysons votre situation et vous proposons une stratégie sur mesure." },
  { q: "Quel est le coût d'un audit énergétique ?", a: "Le coût varie selon la taille et la complexité du bâtiment. Dans de nombreux cas, l'audit peut être partiellement financé. Contactez-nous pour une estimation personnalisée." },
];

export const ARTICLES = [
  {
    slug: "evolutions-decret-tertiaire-2025",
    category: "Réglementation",
    date: "12 novembre 2025",
    title: "Décret Tertiaire : ce qui change pour vos échéances",
    excerpt: "Nouvelles modalités de déclaration sur OPERAT, ajustements des valeurs absolues et points de vigilance pour rester conforme.",
    image: "https://images.unsplash.com/photo-1558706078-c66576fb1e77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: [
      "Le Décret Tertiaire continue de structurer la trajectoire de sobriété énergétique des bâtiments à usage tertiaire. Les gestionnaires doivent déclarer chaque année leurs consommations sur la plateforme OPERAT gérée par l'ADEME.",
      "Deux méthodes coexistent : la réduction relative (par rapport à une année de référence) et la valeur absolue fixée par catégorie d'activité. Le choix de la stratégie a un impact direct sur les investissements à programmer.",
      "Notre recommandation : anticiper l'échéance 2030 dès maintenant en engageant un audit et en priorisant les actions à fort retour sur investissement, notamment la GTB et l'optimisation des équipements.",
    ],
  },
  {
    slug: "nouvelles-fiches-cee-batiment-tertiaire",
    category: "CEE",
    date: "28 octobre 2025",
    title: "Nouvelles fiches CEE : opportunités pour le tertiaire",
    excerpt: "Revue des fiches d'opérations standardisées récemment mises à jour et de leur potentiel de financement.",
    image: "https://images.unsplash.com/photo-1764885518069-84608997f900?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: [
      "Les fiches d'opérations standardisées définissent les montants de primes CEE mobilisables pour chaque type de travaux. Plusieurs fiches du secteur tertiaire ont récemment été révisées.",
      "La régulation par GTB (fiche BAT-TH-116) reste l'un des dispositifs les plus attractifs, avec un retour sur investissement souvent inférieur à trois ans.",
      "Notre équipe assure une veille permanente pour maximiser les primes mobilisables sur chacun de vos projets.",
    ],
  },
  {
    slug: "aides-disponibles-renovation-energetique",
    category: "Financement",
    date: "9 octobre 2025",
    title: "Panorama des aides à la rénovation énergétique",
    excerpt: "CEE, aides territoriales, dispositifs de l'État : comment combiner les financements pour optimiser votre projet.",
    image: "https://images.unsplash.com/photo-1605022582293-b709bdad4f91?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: [
      "Financer sa rénovation énergétique passe par la combinaison intelligente de plusieurs dispositifs. Les CEE constituent le socle, complété par des aides régionales et sectorielles.",
      "Le cumul des aides est encadré mais souvent possible. Un montage optimisé peut couvrir une part très significative du coût des travaux.",
      "Nous construisons pour vous le plan de financement le plus avantageux, dossier compris.",
    ],
  },
  {
    slug: "retour-experience-gtb-campus-tertiaire",
    category: "Retour d'expérience",
    date: "22 septembre 2025",
    title: "Retour d'expérience : GTB sur un campus tertiaire",
    excerpt: "Comment le déploiement d'une Gestion Technique du Bâtiment a permis 28% d'économies en un an.",
    image: "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: [
      "Sur un campus tertiaire de 18 000 m², le déploiement d'une GTB centralisée a permis de piloter finement chauffage, ventilation et éclairage.",
      "Résultat : 28% d'économies d'énergie constatées sur la première année, sans travaux lourds sur le bâti.",
      "Le projet a été intégralement accompagné par PRIMEC2E, du diagnostic au montage CEE et au suivi des performances.",
    ],
  },
  {
    slug: "innovations-efficacite-energetique",
    category: "Innovation",
    date: "5 septembre 2025",
    title: "Les innovations qui transforment l'efficacité énergétique",
    excerpt: "Intelligence artificielle, jumeaux numériques et récupération de chaleur : les technologies qui font la différence.",
    image: "https://images.unsplash.com/photo-1758789667762-56175fe4601c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: [
      "L'efficacité énergétique bénéficie de nouvelles technologies : algorithmes prédictifs, jumeaux numériques et capteurs connectés.",
      "Ces outils permettent d'anticiper les consommations et d'optimiser le fonctionnement des bâtiments en temps réel.",
      "PRIMEC2E intègre ces innovations dans ses stratégies de performance pour maximiser les résultats.",
    ],
  },
  {
    slug: "sante-tertiaire-enjeux-energetiques",
    category: "Secteur",
    date: "21 août 2025",
    title: "Établissements de santé : des enjeux énergétiques spécifiques",
    excerpt: "Continuité de service, contraintes sanitaires et forte consommation : les leviers propres au secteur de la santé.",
    image: "https://images.unsplash.com/photo-1587351021821-f871837248c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: [
      "Les établissements de santé présentent des consommations énergétiques élevées et des contraintes fortes de continuité de service.",
      "L'optimisation passe par une approche sur mesure : récupération de chaleur, régulation fine et production d'énergie sur site.",
      "Nos experts conçoivent des solutions adaptées aux exigences sanitaires et réglementaires du secteur.",
    ],
  },
];

// Simulator reference data (kWh/m²/an baseline by building type)
export const BUILDING_TYPES = [
  { value: "bureau", label: "Bureaux", intensity: 210 },
  { value: "ecole", label: "Enseignement", intensity: 160 },
  { value: "hopital", label: "Santé / Hôpital", intensity: 380 },
  { value: "commerce", label: "Commerce", intensity: 240 },
  { value: "entrepot", label: "Entrepôt / Logistique", intensity: 130 },
  { value: "hotel", label: "Hôtellerie", intensity: 300 },
];

export const ENERGY_PRICE = 0.18; // €/kWh moyen tertiaire
